import { useState } from "react";
import { customerSkillTitle, customerSkillText, isClientVisibleSkill } from "@/components/agent/skillPresentation";
import { ChevronDown, Layers3 } from "lucide-react";

import { HERO_BACKGROUND, HeroPill, Panel, PanelTitle } from "@/components/panels";
import {
  actionBreakdown,
  actionTone,
  countUpdatesSince,
  dailyCounts,
  formatActionLabel,
  formatDriftLabel,
  formatPromotionLabel,
  groupSkillChanges,
} from "@/components/agent/skillUpdates";

const INITIAL_DAYS = 3;
const WINDOW_DAYS = 7;
const BAR_HEIGHT = 56;

// "Updated by GrowthOS" -> "updated by GrowthOS": only the first letter drops, so the product name keeps its capitals.
const midSentence = (label) => label.charAt(0).toLowerCase() + label.slice(1);

function StateChip({ children, tone = "neutral" }) {
  const styles = tone === "good"
    ? "border-[#22C55E]/25 bg-[#22C55E]/[0.08] text-[#16A34A]"
    : tone === "warn"
      ? "border-[#F59E0B]/30 bg-[#F59E0B]/[0.09] text-[#D97706]"
      : "border-sm-ink/10 bg-sm-ink/[0.04] text-sm-ink/55";
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${styles}`}>{children}</span>;
}

function ChangeRow({ change }) {
  const promotion = formatPromotionLabel(change.promotionStatus);
  const drift = formatDriftLabel(change.driftStatus);
  return (
    <li className="flex gap-3 py-3">
      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sm-accent/70" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-sm-ink">{customerSkillTitle(change)}</span>
          <StateChip tone={actionTone(change.action)}>{formatActionLabel(change.action)}</StateChip>
          {promotion ? <StateChip tone="good">{promotion}</StateChip> : null}
          {drift ? <StateChip tone="warn">{drift}</StateChip> : null}
        </div>
        {change.whatChanged ? (
          <p className="mt-1 text-xs leading-relaxed text-sm-ink/75">{customerSkillText(change.whatChanged)}</p>
        ) : (
          <p className="mt-1 text-xs italic leading-relaxed text-sm-ink/60">No change summary was reported for this update.</p>
        )}
        {change.whyChanged ? (
          <p className="mt-1 text-xs leading-relaxed text-sm-ink/65"><span className="font-semibold text-sm-ink/80">Why: </span>{customerSkillText(change.whyChanged)}</p>
        ) : null}
      </div>
    </li>
  );
}

function BatchRow({ entry }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="py-3">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        data-testid={`skill-update-batch-${entry.key}`}
        className="flex w-full items-start gap-3 text-left"
      >
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-sm-ink/[0.05] text-sm-ink/50">
          <Layers3 size={13} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-sm-ink">
              {entry.count} skills {midSentence(formatActionLabel(entry.action))} together
            </span>
          </span>
          <span className="mt-1 block text-xs leading-relaxed text-sm-ink/65">
            These changes were recorded at the same time.
          </span>
        </span>
        <ChevronDown size={15} className={`mt-1 shrink-0 text-sm-ink/35 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <ul className="mt-1 divide-y divide-sm-ink/[0.06] border-l border-sm-ink/10 pl-4">
          {entry.changes.map((change) => <ChangeRow key={change.id} change={change} />)}
        </ul>
      ) : null}
    </li>
  );
}

/** One bar per day for the last week, today's in full white, the rest translucent; the tallest fills the height. */
function DayBars({ days }) {
  const most = Math.max(1, ...days.map((day) => day.count));
  return (
    <div
      className="min-w-[230px] flex-1 md:max-w-[380px]"
      role="img"
      aria-label={`Skill updates per day over the last ${days.length} days: ${days.map((day) => `${day.date} ${day.count}`).join(", ")}`}
      data-testid="agent-activity-bars"
    >
      <div className="flex items-end gap-2">
        {days.map((day) => (
          <div key={day.key} className="flex min-w-0 flex-1 flex-col items-center gap-1.5" title={`${day.date}: ${day.count} ${day.count === 1 ? "change" : "changes"}`}>
            <span className={`text-[11px] font-bold leading-none tabular-nums ${day.today ? "text-white" : "text-white/55"}`}>{day.count || ""}</span>
            <span
              className={`w-full rounded-[4px] ${day.today ? "bg-white" : day.count ? "bg-white/40" : "bg-white/15"}`}
              style={{ height: day.count ? Math.max(5, Math.round((BAR_HEIGHT * day.count) / most)) : 4 }}
            />
            <span className={`text-[10px] font-bold leading-none ${day.today ? "text-white" : "text-white/50"}`}>{day.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The navy band that makes the log stand out: the week's count at figure size, what kinds of changes they were,
 * a bar per day, and the line that says the agent improves itself.
 */
function ActivityBand({ agentName, changes }) {
  const count = countUpdatesSince(changes, { days: WINDOW_DAYS });
  const breakdown = actionBreakdown(changes, { days: WINDOW_DAYS });
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 text-white md:p-6"
      style={{ background: HERO_BACKGROUND, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.14), 0 18px 40px -24px rgba(15,36,66,0.55)" }}
      data-testid="agent-activity-tile"
    >
      <div className="flex flex-wrap items-end justify-between gap-x-10 gap-y-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-white/65">{`Last ${WINDOW_DAYS} days`}</span>
            {count > 0 ? <HeroPill tone="on" dot testId="agent-activity-pill">SELF-IMPROVING</HeroPill> : null}
          </div>
          <div className="mt-3 flex items-baseline gap-2.5">
            <span data-testid="agent-activity-count" className="font-display text-[44px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">{count}</span>
            <span className="text-[13px] text-white/75">{count === 1 ? "skill update" : "skill updates"}</span>
          </div>
          {breakdown.length ? (
            <p data-testid="agent-activity-breakdown" className="mt-2.5 text-[13px] font-bold text-white/90">
              {breakdown.map((part) => `${part.count} ${midSentence(part.label)}`).join(" · ")}
            </p>
          ) : null}
        </div>
        <DayBars days={dailyCounts(changes, { days: WINDOW_DAYS })} />
      </div>
      <p className="mt-5 border-t border-white/15 pt-3.5 text-xs leading-relaxed text-white/70">
        {`${agentName} learns from the work you approve and keeps its own skills up to date. Every change is listed here with what changed and why.`}
      </p>
    </div>
  );
}

/**
 * The agent's skill history as its own panel: the week's tile on the left, then every change a day at a time,
 * newest first, batched maintenance folded, the older days behind "Show all".
 */
export default function SkillActivityLog({ agentName, changes, sourceStatus = "available", className = "" }) {
  const [expanded, setExpanded] = useState(false);
  const clientChanges = (changes ?? []).filter(isClientVisibleSkill);
  const days = groupSkillChanges(clientChanges);
  const visibleDays = expanded ? days : days.slice(0, INITIAL_DAYS);

  return (
    <Panel className={className} data-testid="agent-skill-updates" aria-labelledby="agent-updates-title">
      <PanelTitle
        id="agent-updates-title"
        aside={days.length > INITIAL_DAYS ? (
          <button type="button" data-testid="skill-updates-toggle" onClick={() => setExpanded((value) => !value)} className="min-h-[32px] rounded-lg px-1.5 text-xs font-bold text-sm-primary hover:text-sm-ink">
            {expanded ? "Show recent" : `Show all ${days.length} days`}
          </button>
        ) : null}
      >
        Skill activity log
      </PanelTitle>

      {sourceStatus === "error" ? (
        <p className="mt-3 text-[13px] leading-relaxed text-sm-ink/70">Recent updates could not be loaded. The skill change history is temporarily unreadable. Nothing has been substituted in its place.</p>
      ) : days.length === 0 ? (
        <p className="mt-3 text-[13px] leading-relaxed text-sm-ink/70">No skill updates reported yet. When your agent's skills are added, improved, or removed, each change will be listed here with what changed and why.</p>
      ) : (
        <>
          <div className="mt-4">
            <ActivityBand agentName={agentName} changes={clientChanges} />
          </div>
          <div className="mt-2 grid items-start gap-x-8 md:grid-cols-2">
            {visibleDays.map((day) => (
              <div key={day.key} className="border-t border-sm-ink/[0.08] pt-3 first:border-t-0 first:pt-2 [&:not(:last-child)]:pb-2" data-testid={`skill-update-day-${day.key}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] font-bold text-sm-ink/70">{day.label}</span>
                  <span className="text-xs tabular-nums text-sm-ink/55">{day.total} {day.total === 1 ? "change" : "changes"}</span>
                </div>
                <ul className="mt-0.5 divide-y divide-sm-ink/[0.06]">
                  {day.entries.map((entry) => (entry.type === "batch"
                    ? <BatchRow key={entry.key} entry={entry} />
                    : <ChangeRow key={entry.key} change={entry.change} />))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}
